'use client';

import { IconChevronRightFilled } from '@zillow/constellation-icons/react/filled/icon-chevron-right-filled';
import { interactionResponse } from '@zillow/yield-callback';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { PhotoCarouselRootContext } from './context/photo-carousel-root.context';
import { photoCarouselNavButtonRecipe } from './styles/photo-carousel-nav-button.styles';
import { photoCarouselNextButtonStyles } from './styles/photo-carousel-next-button.styles';
import type { PhotoCarouselNextButtonPropsInterface } from './types/photo-carousel-next-button.types';

/**
 * Control used to show the next photo in the carousel.
 *
 * Renders as a span instead of a button and uses `aria-hidden` so that it's
 * hidden from the tab order (keyboard controls are provided by `PhotoCarousel.Dots` instead).
 *
 * @related ['IconButton']
 * @since 10.0.0
 */

export const PhotoCarouselNextButton = forwardRef<
  HTMLButtonElement,
  PhotoCarouselNextButtonPropsInterface
>((props: PhotoCarouselNextButtonPropsInterface, forwardedRef) => {
  const {
    activeIndex = 0,
    shouldAwaitInteractionResponse,
    shouldLoop = true,
    totalSlides = 0,
    updateActiveIndex,
  } = useContext(PhotoCarouselRootContext);
  const {
    children = (
      <Icon>
        <IconChevronRightFilled />
      </Icon>
    ),
    css: cssProp,
    disabled = !shouldLoop && activeIndex === totalSlides - 1,
    onClick,
    ...rest
  } = props;

  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      // Stop propagation immediately to prevent invoking Property Card's `onClick`
      event.stopPropagation();

      if (disabled) return;

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      updateActiveIndex?.(activeIndex + 1);
      onClick?.(event);
    },
    [activeIndex, onClick, shouldAwaitInteractionResponse, updateActiveIndex, disabled],
  );

  return (
    <IconButton
      data-c11n-component="PhotoCarousel.NextButton"
      disabled={disabled}
      emphasis="bare"
      onClick={handleClick}
      onImpact
      ref={forwardedRef}
      tabIndex={-1}
      title="Next photo"
      tone="neutral"
      css={{
        ...photoCarouselNavButtonRecipe.raw({ disabled }),
        ...photoCarouselNextButtonStyles,
        ...cssProp,
      }}
      {...rest}
    >
      {children}
    </IconButton>
  );
});

PhotoCarouselNextButton.displayName = 'PhotoCarousel.NextButton';
